/**
 * Operations User Permission Row
 * Single user row with name, email, and permission toggles
 */

import { useTranslation } from 'react-i18next';
import { Switch } from '@/components/ui/switch';
import { TableRow, TableCell } from '@/components/ui/table';
import { Loader2 } from 'lucide-react';
import {
  OPERATIONS_PERMISSIONS,
  hasOperationsPermission,
  togglePermission,
  useUpdateUserPermissions,
  type OperationsPermissionKey,
} from '../../hooks/useOperationsPermissionManagement';
import type { OperationsUserWithPermissions } from '../../api';

interface OperationsUserPermissionRowProps {
  user: OperationsUserWithPermissions;
}

export function OperationsUserPermissionRow({ user }: OperationsUserPermissionRowProps) {
  const { t } = useTranslation('operations');
  const updatePermissions = useUpdateUserPermissions();

  const handleToggle = (permission: OperationsPermissionKey, enabled: boolean) => {
    const newPermissions = togglePermission(
      user.permission_strings,
      permission,
      enabled
    );
    updatePermissions.mutate({
      userId: user._id,
      permissionStrings: newPermissions,
      permissionKey: permission,
    });
  };

  // Check if a specific permission is currently being updated for this user
  const isPermissionUpdating = (permissionKey: string): boolean => {
    return (
      updatePermissions.isPending &&
      updatePermissions.variables?.userId === user._id &&
      updatePermissions.variables?.permissionKey === permissionKey
    );
  };

  const displayName =
    user.firstname || user.lastname
      ? `${user.firstname} ${user.lastname}`.trim()
      : user.email;

  return (
    <TableRow>
      <TableCell className="font-medium">
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-900">{displayName}</span>
          <span className="text-xs text-gray-500">{user.email}</span>
        </div>
      </TableCell>
      {OPERATIONS_PERMISSIONS.map((permission) => (
        <TableCell key={permission.key} className="text-center">
          <div className="flex items-center justify-center">
            {isPermissionUpdating(permission.key) ? (
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            ) : (
              <Switch
                checked={hasOperationsPermission(user, permission.key)}
                onCheckedChange={(checked) => handleToggle(permission.key, checked)}
                aria-label={t(permission.labelKey)}
                disabled={updatePermissions.isPending}
              />
            )}
          </div>
        </TableCell>
      ))}
    </TableRow>
  );
}
